import './AggregateResultModalContent.css';

import { Fragment, useMemo } from 'react';
import ModalBody from '../ControlActionModalContent/components/ModalBody/ModalBody';
import Button from '../../Button/Button';
import CellContent from '../../Manager/Grid/components/CellContent/CellContent';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useModalKeyboard } from '../../../hooks/shortcuts/useModalKeyboard';
import { useUserState } from '../../../hooks/context/useUserState';
import { getFormattedAggregateRows } from './utils/getFormattedAggregateRows';
import { groupAggregateRowsByLabel } from './utils/groupAggregateRowsByLabel';
import { AggregateControlAction } from '../../../types/types';
import {
  AggregateResultItem,
  AggregateRowGroup,
  AggregateRowGroupValue,
} from './types';

export interface AggregateResultModalContentProps {
  header: string;
  items: AggregateResultItem[];
  onClose: () => void;
  aggregateItems?: AggregateControlAction['items'];
}

const AggregateResultModalContent = ({
  header,
  items,
  onClose,
  aggregateItems,
}: AggregateResultModalContentProps) => {
  const { t } = useTranslate();
  const {
    state: { defaultCurrencyFormat, defaultNumberFormat },
  } = useUserState();

  useModalKeyboard({ onEnter: onClose, onEscape: onClose });

  const groups: AggregateRowGroup[] = useMemo(
    () =>
      groupAggregateRowsByLabel(
        getFormattedAggregateRows(
          items,
          defaultCurrencyFormat,
          defaultNumberFormat,
          aggregateItems
        )
      ),
    [items, defaultCurrencyFormat, defaultNumberFormat, aggregateItems]
  );

  return (
    <ModalBody
      className="aggregate-result-modal"
      header={header}
      text={
        <div className="aggregate-result-table">
          {groups.length === 0 ? (
            <p className="aggregate-result-empty">{t('noRecordsDataset')}</p>
          ) : (
            <table>
              <tbody>
                {groups.map((group, groupIndex) => (
                  <Fragment key={groupIndex}>
                    {group.values.map(
                      (
                        groupValue: AggregateRowGroupValue,
                        valueIndex: number
                      ) => (
                        <tr key={valueIndex}>
                          {valueIndex === 0 && (
                            <td rowSpan={group.values.length}>
                              <CellContent value={group.label} />
                            </td>
                          )}
                          {groupValue.label != null ? (
                            <>
                              <td>
                                <CellContent value={groupValue.label} />
                              </td>
                              <td className="aggregate-result-value">
                                <CellContent value={groupValue.value} />
                              </td>
                            </>
                          ) : (
                            <td colSpan={2} className="aggregate-result-value">
                              <CellContent value={groupValue.value} />
                            </td>
                          )}
                        </tr>
                      )
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      }
      buttons={<Button onClick={onClose}>{t('close')}</Button>}
    />
  );
};

export default AggregateResultModalContent;
